import { Injectable } from '@angular/core';
import { Firestore, doc, updateDoc, deleteDoc, collection, query, where, getDocs, writeBatch } from '@angular/fire/firestore';
import { User } from '../entities/user.model';
import { AuthService } from './auth.service';
import { FlashcardService } from './flashcard.service';
import { Flashcard, WordCard } from '../entities/wordcard.model';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  public touring: boolean = false; // Flag to indicate if the user is currently touring

  constructor(private firestore: Firestore, 
    private authService: AuthService,
    private flashCardService: FlashcardService
  ) {}

  async applyChanges(user: User): Promise<void> {
    const userDocRef = doc(this.firestore, `users/${user.uid}`);
    const updateData: any = {
      nativeLanguage: user.nativeLanguage,
      targetLanguage: user.targetLanguage,
      level: user.level
    };

    // Only include optional fields if they are defined
    if (user.textSize !== undefined) {
      updateData.textSize = user.textSize;
    }
    if (user.darkMode !== undefined) {
      updateData.darkMode = user.darkMode;
    }

    await updateDoc(userDocRef, updateData);

    this.authService.setUser(user);
  }

  checkIfWordInVocab(word: string, languageCode: string): boolean {
    const user = this.authService.currentUserValue;
    if (!user || !user.vocab || !user.vocab[languageCode]) {
      return false;
    }
    const wordsMap = user.vocab[languageCode].words;
    return Object.keys(wordsMap).includes(word);
  }
  // addWordToVocab which takes in a WordCard, creates a Flashcard for it and adds it to the user's vocabulary
  async addWordToVocab(wordCard: WordCard, targetLanguage: string, articleId: string): Promise<string> {
    const user = this.authService.currentUserValue;
    if (!user) return "notSignedIn";
    if (!user.email) return "temporaryUser";
    try {
      if (!user.vocab) {
        user.vocab = {};
      }
      if (!user.vocab[targetLanguage]) {
        user.vocab[targetLanguage] = { words: {} };
      }
      // Create a Flashcard from the WordCard
      const flashCard: Flashcard | null = await this.flashCardService.createFlashCard(wordCard, articleId, user);
      if (!flashCard || !flashCard.id) {
        return "flashCardError";
      }
      // Add the Flashcard to the user's vocabulary
      if (!user.vocab[targetLanguage].words[wordCard.root_word]) {
        user.vocab[targetLanguage].words[wordCard.root_word] = [flashCard.id];
      } else {
        user.vocab[targetLanguage].words[wordCard.root_word] = [
          ...user.vocab[targetLanguage].words[wordCard.root_word],
          flashCard.id
        ];
      }
      // Update the user document in Firestore
      const userDocRef = doc(this.firestore, `users/${user.uid}`);
      await updateDoc(userDocRef, {
        [`vocab.${targetLanguage}.words.${wordCard.root_word}`]: user.vocab[targetLanguage].words[wordCard.root_word]
      });
      return "success";
    } catch (error) {
      return "flashCardError";
    }
  }

  async updateUserTouringStatus(): Promise<void> {
    this.touring = false;
    const user = this.authService.currentUserValue;
    if (user) {
      user.completedTour = true; // Set completedTour to true
      const userDocRef = doc(this.firestore, `users/${user.uid}`);
      await updateDoc(userDocRef, { completedTour: true });
      this.authService.getCurrentUser(); // Refresh the current user
    }
  }

  async toggleBookMark(articleId: string, add: boolean): Promise<void> {
    const user = this.authService.currentUserValue;
    if (!user) {
      throw new Error('No user logged in');
    }
    if (!user.bookmarks) {
      user.bookmarks = [];
    }

    if (add) {
      if (!user.bookmarks.includes(articleId)) {
        user.bookmarks.push(articleId);
      }
    } else {
      user.bookmarks = user.bookmarks.filter(id => id !== articleId);
    }

    const userDocRef = doc(this.firestore, `users/${user.uid}`);
    await updateDoc(userDocRef, { bookmarks: user.bookmarks });
  }

  async deleteUserAccount(): Promise<void> {
    const user = this.authService.currentUserValue;
    if (!user || !user.uid) {
      throw new Error('No user logged in');
    }

    try {
      // Delete all user's flashcards
      const flashcardsQuery = query(
        collection(this.firestore, 'flash_cards'),
        where('userId', '==', user.uid)
      );
      
      const flashcardsDocs = await getDocs(flashcardsQuery);
      const batch = writeBatch(this.firestore);
      
      flashcardsDocs.forEach((doc) => {
        batch.delete(doc.ref);
      });
      
      // Delete the user document
      const userDocRef = doc(this.firestore, `users/${user.uid}`);
      batch.delete(userDocRef);
      
      // Commit all deletions
      await batch.commit();
      
      console.log('User account and all related data deleted successfully');
    } catch (error) {
      console.error('Error deleting user account:', error);
      throw error;
    }
  }
}
